import { Fragment, useMemo } from "react";
import { ProjectJob, ProjectState } from "../Project";
import { PhasePlanning } from "./HorizontalPlanningCalendar";
import dayjs, { Dayjs } from "dayjs";
import { TimespanGrid } from "./TimespanGrid";
import ApiService from "@/api/ApiService";
import { AxiosResponse } from "axios";
import { useQuery } from "@tanstack/react-query";
import { textColors } from "@/shared/forms/ColorPicker";
import { Add, DataArray, Event } from "@mui/icons-material";
import { compressBookingsByAdjacentDays } from "./compressBookingsByAdjacentDays";
import { ProcessedBooking, ProcessedOrder } from "./BookingOrderCombiner";
import JobInner from "./JobInner";
import PhaseBar from "./PhaseBar";
import { ChevronsLeftRightEllipsis, ChevronsRightLeft } from "lucide-react";
import { useDeviceInfoModal } from "@/components/device/DeviceInfoModal";
import ProjectNoteBar from "./ProjectNoteBar";

type RowInfo = {
  startCol: number;
  endCol: number;
  start: Dayjs;
  end: Dayjs;
  isBegin: boolean;
  isEnd: boolean;
};

interface TimespanBase {
  id: string;
  row: number;
  colStart: number;
  colEnd: number;
  isBegin: boolean;
  isEnd: boolean;
  type: "job" | "resourceBooking" | "resourceOrder" | "phase" | "createJob" | "downtime" | "note";
  data: any;
  rowEnd?: number;
}

/**
 * Maps a date to weekday number with Monday=2 ... Sunday=8
 * Uses dayjs().day(): Sunday=0, Monday=1, ... Saturday=6
 */
const toMon2Sun8 = (d: Dayjs): number => {
  const w = d.day(); // 0..6 (Sun..Sat)
  return w === 0 ? 8 : w + 1; // Mon(1)->2, Tue(2)->3, ..., Sat(6)->7, Sun(0)->8
};

/**
 * Returns capped start/end within [weekStart, weekEnd] and flags for whether the original
 * start/end begin/end within the same week slice.
 *
 * Rules:
 * - start is capped to max(start, weekStart)
 * - end is capped to min(end, weekEnd)
 * - Monday -> 2, Tuesday -> 3, ..., Sunday -> 8
 * - isBegin is TRUE if start was not capped to weekStart
 *   (i.e., start >= weekStart; if start === weekStart, it is also TRUE)
 * - isEnd is TRUE if end was not capped to weekEnd
 *   (i.e., end <= weekEnd; if end === weekEnd, it is also TRUE)
 */
export const getRows = (start: string | Dayjs, end: string | Dayjs, weekStart: Dayjs, weekEnd: Dayjs): RowInfo => {
  const s = dayjs(start);
  const e = dayjs(end);

  if (!s.isValid() || !e.isValid()) {
    throw new Error("Invalid date string for start or end.");
  }
  if (!weekStart.isValid() || !weekEnd.isValid()) {
    throw new Error("Invalid Dayjs for weekStart or weekEnd.");
  }

  // Cap start to max(start, weekStart); cap end to min(end, weekEnd)
  const cappedStart = s.isBefore(weekStart) ? weekStart : s;
  const cappedEnd = e.isAfter(weekEnd) ? weekEnd : e;

  // Flags:
  // isBegin true if original start >= weekStart (including equality)
  // isEnd   true if original end   <= weekEnd   (including equality)
  const isBegin = !s.isBefore(weekStart);
  const isEnd = !e.isAfter(weekEnd);

  // Map to Monday=2 ... Sunday=8
  const startNum = toMon2Sun8(cappedStart);
  const endNum = toMon2Sun8(cappedEnd);

  return {
    startCol: startNum,
    start: cappedStart,
    end: cappedEnd,
    endCol: endNum,
    isBegin,
    isEnd,
  };
};

export default function HorizontalWeek({
  phases,
  weekStart,
  weekEnd,
  selectDate,
  phaseData,
  rowStart,
  notes,
  writeable,
  saveNote,
}: PhasePlanning & {
  phases: ProjectState["phases"];
  selectDate: (phase: string, date?: string, allowCreation?: boolean, referenceDate?: string) => void;
  writeable: boolean;
  saveNote: (note: ProjectState["notes"][number]) => void;
}) {
  const { showByNumber } = useDeviceInfoModal();
  const timespans: TimespanBase[] = useMemo(() => {
    const timespans: TimespanBase[] = [];
    const weekEndDjs = dayjs(weekEnd, "YYYY-MM-DD").subtract(1, "day");
    const weekStartDjs = dayjs(weekStart, "YYYY-MM-DD");
    const grid = new TimespanGrid();
    let minRow = 0;
    for (const p of phaseData) {
      const phase = phases.find((phase) => phase.uuid === p.uuid);
      if (!phase) continue;
      const bookings = compressBookingsByAdjacentDays<ProcessedOrder | ProcessedBooking>(p.dates);
      const {
        start,
        end,
        startCol: phaseStartCol,
        endCol: phaseEndCol,
        isBegin,
        isEnd,
      } = getRows(p.plannedStart, p.plannedEnd, weekStartDjs, weekEndDjs);
      const phaseRowStart = grid.addTimespan(start, end, phase?.uuid, minRow);
      timespans.push({
        id: phase.uuid + "_" + phaseRowStart,
        row: phaseRowStart,
        colStart: phaseStartCol,

        colEnd: phaseEndCol,
        type: "phase",
        data: {
          uuid: phase.uuid,
          name: phase.name,
          comment: phase.comment,
          color: phase.color,
          startDate: start.format("YYYY-MM-DD"),
          endDate: end.format("YYYY-MM-DD"),
        },
        isBegin,
        isEnd,
      });
      minRow = phaseRowStart + 2;
      let phaseRowEnd = minRow;

      for (let b of bookings) {
        const row = grid.addTimespan(b.start, b.end, b.combinationId, minRow);
        const colStart = toMon2Sun8(b.start);
        const colEnd = toMon2Sun8(b.end);
        timespans.push({
          id: b.combinationId + "_" + b.start.format("YYYY-MM-DD"),
          row,
          colStart,
          colEnd,
          type: b.isOrder ? "resourceOrder" : "resourceBooking",
          data: b,
          isBegin: true,
          isEnd: true,
        });
        phaseRowEnd = Math.max(phaseRowEnd, row);
      }

      for (const [downtimeIndex, downtime] of (phase.downtimes ?? []).filter((item) => !item.deleted).entries()) {
        const { start: downtimeStart, end: downtimeEnd, startCol, endCol, isBegin: downtimeBegin, isEnd: downtimeFinish } =
          getRows(downtime.startDate, downtime.endDate, weekStartDjs, weekEndDjs);
        if (downtimeEnd < weekStartDjs || downtimeStart > weekEndDjs) continue;
        const downtimeKey = downtime.id ? String(downtime.id) : `new-${downtimeIndex}`;
        const downtimeGridId = `downtime-${phase.uuid}-${downtimeKey}`;
        const row = grid.addTimespan(downtimeStart, downtimeEnd, downtimeGridId, minRow);
        timespans.push({
          id: `${downtimeGridId}-${row}`,
          row,
          colStart: startCol,
          colEnd: endCol,
          type: "downtime",
          data: downtime,
          isBegin: downtimeBegin,
          isEnd: downtimeFinish,
        });
        phaseRowEnd = Math.max(phaseRowEnd, row);
      }

      let date = weekStart < p.plannedStart ? dayjs(p.plannedStart) : weekStartDjs;
      const fend = weekEnd > p.plannedEnd ? dayjs(p.plannedEnd).add(1, "day") : weekEndDjs.add(1, "day");
      while (date.isBefore(fend)) {
        const dateStr = date.format("YYYY-MM-DD");
        const col = toMon2Sun8(date);
        const { bookingState = false, saveDate } = p.dates.find((j) => j.date === dateStr) ?? {};
        if (!!saveDate) {
          const job = phase.jobs[saveDate];

          timespans.push({
            id: job.uuid,
            row: phaseRowStart + 1,
            rowEnd: phaseRowEnd,
            colStart: col,
            colEnd: col,
            type: "job",
            data: { ...job, phaseColor: phase.color, phaseUuid: phase.uuid, bookingState },
            isBegin: false,
            isEnd: false,
          });
        } else {
          timespans.push({
            id: p.uuid + "_" + dateStr,
            row: phaseRowStart,
            colStart: col,
            colEnd: col,
            type: "createJob",
            data: { phaseColor: phase.color, phaseUuid: phase.uuid, date: dateStr },
            isBegin: false,
            isEnd: false,
          });
        }
        date = date.add(1, "day");
      }

      minRow = phaseRowEnd + 2;
    }
    for (const note of notes) {
      const { start, end, startCol, endCol, isBegin, isEnd } = getRows(note.startDate, note.endDate, weekStartDjs, weekEndDjs);
      // Newly created notes all have id=0 until the project is saved. clientId is
      // stable from creation onward and therefore must drive collision detection.
      const noteGridId = `note-${note.clientId}`;
      const row = grid.addTimespan(start, end, noteGridId, minRow);
      timespans.push({ id: `${noteGridId}-${weekStart}`, row, colStart: startCol, colEnd: endCol, type: "note", data: { ...note, segmentStart: start.format("YYYY-MM-DD") }, isBegin, isEnd });
    }
    return timespans;
  }, [phaseData, notes, rowStart, weekStart, weekEnd]);

  return timespans.map((span) => {
    switch (span.type) {
      case "job":
        return (
          <Fragment key={span.id}>
            <JobInner
              onClick={() => selectDate(span.data.phaseUuid, span.data.date, false, weekStart)}
              style={{
                backgroundColor: "color-mix(in srgb, " + span.data.phaseColor + " 50%, #FFFFFF 100%)",
                gridColumnStart: String(span.colStart),
                gridColumnEnd: String(span.colEnd + 1),
                gridRow: String(rowStart + span.row),
              }}
              data={span.data as ProjectJob}
            />
            <div
              className={"hw_job_placeholder"}
              style={{
                gridColumnStart: String(span.colStart),
                gridColumnEnd: String(span.colEnd + 1),
                gridRowStart: String(rowStart + span.rowEnd + 2),
                gridRowEnd: String(rowStart + span.rowEnd + 2),
              }}
            />
          </Fragment>
        );
      case "createJob":
        return (
          <div
            key={span.id}
            className={"hw_job_create"}
            style={{
              gridColumnStart: String(span.colStart),
              gridColumnEnd: String(span.colEnd + 1),
              gridRow: String(rowStart + span.row),
            }}
          >
            <div
              onClick={() => selectDate(span.data.phaseUuid, span.data.date, true, weekStart)}
              className={"hw_job_create_button"}
              style={{ backgroundColor: "color-mix(in srgb, " + span.data.phaseColor + " 50%, #FFFFFF 100%)" }}
            >
              <Add fontSize="small" />
            </div>
          </div>
        );
      case "resourceBooking":
        return (
          <div
            key={span.id}
            onClick={span.data.type === "RESOURCE" ? () => showByNumber(span.data.number) : undefined}
            className={
              "hw_resourcebooking" +
              (span.data.type === "RESOURCE" ? " dim_hover" : "") +
              (span.isBegin ? " isBegin" : "") +
              (span.isEnd ? " isEnd" : "") +
              (" hw_rb_" + span.data.type) +
              (span.data.vacation ? " vacation" : "")
            }
            style={{
              gridColumnStart: String(span.colStart),
              gridColumnEnd: String(span.colEnd + 1),
              gridRow: String(rowStart + span.row),
            }}
          >
            {span.data.type === "RESOURCE" ? <span className="bh_number">{span.data.number}</span> : null}
            {span.data.name}
          </div>
        );
      case "resourceOrder":
        return (
          <div
            key={span.id}
            title={(
              (!!span.data.startConstraint ? "ab " + dayjs(span.data.startConstraint).format("DD.MM") + " " : " ") +
              (!!span.data.endConstraint ? "bis " + dayjs(span.data.endConstraint).format("DD.MM") + " " : " ") +
              (span.data.rejected ? span.data.rejection.comment + " (" + span.data.rejection.rejected_by + ")" : "")
            ).trim()}
            className={
              "hw_resourceorder" +
              (span.data.rejected ? " rejected" : "") +
              (span.isBegin ? " isBegin" : "") +
              (span.isEnd ? " isEnd" : "") +
              (" hw_ro_" + span.data.type)
            }
            style={{
              gridColumnStart: String(span.colStart),
              gridColumnEnd: String(span.colEnd + 1),
              gridRow: String(rowStart + span.row),
            }}
          >
            {span.data.tagName}
            {!span.data.icon ? null : span.data.icon === "day" ? (
              <Event sx={{ color: "#444", ml: "2px" }} fontSize="inherit" />
            ) : (
              <ChevronsRightLeft color="#444" size={13} style={{ marginLeft: "2px" }} />
            )}
          </div>
        );
      case "phase":
        return <PhaseBar key={span.id} onClick={() => selectDate(span.data.uuid, undefined, false, weekStart)} span={span} rowStart={rowStart} />;
      case "downtime":
        return <div
          key={span.id}
          className={`hw_phase hw_note${span.isBegin ? " isBegin" : ""}${span.isEnd ? " isEnd" : ""}`}
          title={`Stillstand: ${span.data.comment}`}
          style={{
            gridRow: span.row + rowStart,
            gridColumn: `${span.colStart} / ${span.colEnd + 1}`,
            backgroundColor: "#ed6c02",
            color: "white",
          }}
        >
          <div className="hw_phase_name">
            <div className="hw_phase_namepart">Stillstand: {span.data.comment}</div>
          </div>
        </div>;
      case "note":
        return <ProjectNoteBar key={span.id} note={span.data} span={span} rowStart={rowStart} writeable={writeable} onSave={saveNote} />;
    }
  });
}
